import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  parseFractionLikeInput as parseFraction,
  formatLatexPreview,
} from "../../../../components/inputs/FractionInput";
import CircleEqualTangentsDiagram from "../../../../components/diagrams/CircleEqualTangentsDiagram";

const DEC_TOL = 0.01;
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

function formatX0(x0Latex) {
  // Jeśli zaczyna się od "-", to dajemy nawiasy
  return x0Latex.trim().startsWith("-") ? `(${x0Latex})` : x0Latex;
}

function coeffTimesX0Latex(a, x0Latex) {
  const val = formatX0(x0Latex);
  if (a === 1) return `${val}`;                // np. (2/3)
  if (a === -1) return `(-1)\\cdot ${val}`;    // (-1)·(2/3)
  if (a < 0) return `(${a})\\cdot ${val}`;     // (-5)·(2/3)
  return `${a}\\cdot ${val}`;                  // 5·(2/3)
}


/* ax + b -> "ax+b" z poprawnymi znakami i skracaniem 1x/-1x/±b=0 */
function linStr(a, b) {
  const ax = a === 1 ? "x" : a === -1 ? "-x" : `${a}x`;
  if (b === 0) return ax;
  return b > 0 ? `${ax}+${b}` : `${ax}-${-b}`;
}

/* gcd i ładny LaTeX: ułamek → liczba mieszana, minus przed CAŁOŚCIĄ */
const gcd = (a, b) => (b ? gcd(b, a % b) : Math.abs(a));

function toMixedLatex(num, den) {
  if (den === 0) return "\\text{nieokreślone}";
  // znak na zewnątrz
  let sign = num === 0 ? "" : (num < 0) ^ (den < 0) ? "-" : "";
  let n = Math.abs(Math.round(num));
  let d = Math.abs(Math.round(den));
  const g = gcd(n, d) || 1;
  n = n / g;
  d = d / g;

  const whole = Math.floor(n / d);
  const rem = n % d;

  let body;
  if (rem === 0) {
    body = `${whole}`;
  } else if (whole > 0) {
    body = `${whole}\\,\\dfrac{${rem}}{${d}}`;
  } else {
    body = `\\dfrac{${n}}{${d}}`;
  }
  return `${sign}${body}`;
}

/* Proste pomocnicze do LaTeX-a */
const frac = (n, d) => `\\dfrac{${n}}{${d}}`;
const fmtAx = (a, v = "x") => (a === 1 ? v : a === -1 ? `-${v}` : `${a}${v}`);
const fmtB = (b) => (b === 0 ? "" : b > 0 ? `+${b}` : `-${-b}`);
const mulFrac = (a, n, d) => {
  if (a === 1) return frac(n, d);
  if (a === -1) return `-${frac(n, d)}`;
  return `${a}\\cdot${frac(n, d)}`;
};

/* Usuwa z przodu dowolny łańcuch "zmienna (=|≈|~|:) " (np. "x= a≈ m~  ...") */
function sanitizeAnswerRaw(raw) {
  if (!raw) return "";
  let s = String(raw).trim().replace(",", ".");
  s = s.replace(/^(?:[a-zA-Z][a-zA-Z0-9_]*\\s*(?:=|≈|~|:)\\s*)+/, "");
  return s.trim();
}

/** GENERATOR: a1,a2 ∈ {-5..-1,1..5}, a1 ≠ a2; b1,b2 ∈ {-12..12}; x0 może być ułamkiem */
export function generateTask() {
  const A_POOL = [-5, -4, -3, -2, -1, 1, 2, 3, 4, 5];
  const B_POOL = Array.from({ length: 25 }, (_, i) => i - 12); // -12..12

  for (let tries = 0; tries < 500; tries++) {
    const a1 = pick(A_POOL);
    let a2 = pick(A_POOL);
    if (a2 === a1) continue;

    const b1 = pick(B_POOL);
    const b2 = pick(B_POOL);

    const den = a1 - a2;
    if (den === 0) continue;

    const num = b2 - b1;
    const x0 = num / den;

    if (!(x0 >= -10 && x0 <= 20)) continue;

    const L1 = a1 * x0 + b1;
    const L2 = a2 * x0 + b2;
    if (!(L1 > 2 && L1 < 40)) continue;
    if (!(L2 > 2 && L2 < 40)) continue;

    const expr1 = linStr(a1, b1);
    const expr2 = linStr(a2, b2);

    return {
      type: "FindXFromEqualTangentsTask",
      data: {
        a1,
        b1,
        a2,
        b2,
        x0,
        x0Latex: toMixedLatex(num, den),
        L: L1,
        expr1,
        expr2,
      },
    };
  }

  // Fallback
  const a1 = 3,
    b1 = 1,
    a2 = 5,
    b2 = 7; // x = (7-1)/(3-5) = 6/(-2) = -3
  const num = b2 - b1,
    den = a1 - a2;
  const x0 = num / den;
  return {
    type: "FindXFromEqualTangentsTask",
    data: {
      a1,
      b1,
      a2,
      b2,
      x0,
      x0Latex: toMixedLatex(num, den),
      L: a1 * x0 + b1,
      expr1: linStr(a1, b1),
      expr2: linStr(a2, b2),
    },
  };
}

/* ===== TREŚĆ ===== */
export function renderTask({ data }) {
  const { expr1, expr2 } = data;
  return (
    <div style={{ marginTop: 8 }}>
      Z punktu zewnętrznego <InlineMath math={"P"} /> poprowadzono do okręgu dwie styczne
      w punktach <InlineMath math={"A"} /> i <InlineMath math={"B"} />.
      Wiadomo, że <InlineMath math={`\\;|PA| = ${expr1}`} /> oraz{" "}
      <InlineMath math={`\\;|PB| = ${expr2}`} />.
      Oblicz wartość <InlineMath math={"x"} />.
      <div
        style={{
          marginTop: 15,
          marginBottom: 25,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <CircleEqualTangentsDiagram expr1={expr1} expr2={expr2} width={640} height={360} />
      </div>
    </div>
  );
}

/* ===== INPUT ===== */
export function renderInput(_task, userAnswer, onChange, disabled) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw ?? "";
  const normalize = (v) => (typeof v === "string" ? v : v?.raw ?? "");
  return (
    <FractionInput
      value={raw}
      onChange={(v) => onChange({ raw: normalize(v) })}
      disabled={disabled}
      label="x"
      placeholder="np. 7/3, -5/2, 1 1/6"
    />
  );
}

/* ===== CHECK ===== */
export function checkAnswer({ data }, userAnswer) {
  const expected = data.x0;
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw ?? "";
  const cleaned = sanitizeAnswerRaw(raw);
  if (!cleaned) return false;
  const val = parseFraction(cleaned);
  if (!Number.isFinite(val)) return false;
  return Math.abs(val - expected) <= DEC_TOL;
}

/* ===== ANSWER ===== */
export function renderAnswer({ data }) {
  return <InlineMath math={`x=${data.x0Latex}`} />;
}


/* ===== EXPLANATION ===== */
export function renderExplanation({ data }) {
  const { a1, b1, a2, b2 } = data;
  const num = b2 - b1;
  const den = a1 - a2;

  // Linie równań w ładnej postaci (bez 1x/-1x i bez nawiasów)
  const lhs1 = `${fmtAx(a1)} ${fmtB(b1)}`.replace("  ", " ");
  const rhs1 = `${fmtAx(a2)} ${fmtB(b2)}`.replace("  ", " ");
  const line2 = `${fmtAx(a1)} ${fmtB(b1)} = ${fmtAx(a2)} ${fmtB(b2)}`.replaceAll("  ", " ");
  const line3 = `${fmtAx(a1 - a2)} = ${b2 - b1}`; // bez nawiasów przy współczynniku

  // Decyzja, czy pokazać dwa zapisy ułamka, czy tylko jeden
  const g = gcd(Math.abs(num), Math.abs(den)) || 1;
  const baseFrac = frac(num, den);
  const simplified = toMixedLatex(num, den);
  const showTwo = !(g === 1 && Math.abs(num) < Math.abs(den) && num >= 0 && den > 0);
  const lastLine = showTwo ? `x = ${baseFrac} = ${simplified}` : `x = ${simplified}`;

  // Sprawdzenie – dokładne podstawienie, każda styczna w osobnej linii (bez zaokrągleń)
  // |PA| = a1*x + b1 = a1*(num/den) + b1 = (a1*num + b1*den)/den = mixed
  const L1num = a1 * num + b1 * den;
  const L2num = a2 * num + b2 * den;
  const Lden = den;

const PAline =
  `|PA| = ${fmtAx(a1)} ${fmtB(b1)} = ${coeffTimesX0Latex(a1, data.x0Latex)} ${fmtB(b1)} = ${toMixedLatex(L1num, Lden)}`;

const PBline =
  `|PB| = ${fmtAx(a2)} ${fmtB(b2)} = ${coeffTimesX0Latex(a2, data.x0Latex)} ${fmtB(b2)} = ${toMixedLatex(L2num, Lden)}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>
        <strong>Krok 1.</strong> Dwie styczne z jednego punktu mają równe długości:
        <InlineMath math={"\\;|PA|=|PB|"} />.
      </div>

      <div style={{ marginTop: 8 }}>
        <strong>Krok 2.</strong> Podstawiamy wyrażenia i rozwiązujemy równanie liniowe.
      </div>
      <BlockMath math={`${lhs1} = ${rhs1}`} />
      <BlockMath math={line2} />
      <BlockMath math={line3} />
      <BlockMath math={lastLine} />

      <div style={{ marginTop: 10 }}>
        <strong>Sprawdzenie.</strong>
      </div>
      <BlockMath math={PAline} />
      <BlockMath math={PBline} />
    </div>
  );
}

/* ===== PREVIEW UŻYTKOWNIKA ===== */
export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw ?? "";
  const cleaned = sanitizeAnswerRaw(raw);
  if (!cleaned) return <i>(brak odpowiedzi)</i>;
  // formatLatexPreview z nazwą zmiennej — zwraca "x = …" bez zbędnych prefiksów
  const latex = formatLatexPreview(cleaned, "x");
  return <InlineMath math={latex} />;
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
